import { SHADE_SOFT, TAU, WHITE, circle, glow } from '../paint';
import type { Ctx, Piece } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';

export const UNIT = 2;

const BARK = '#8a5a3a';
const GRAIN = '#5e3a22';
const OUT = 'rgba(4,6,18,.9)';

export const KNEES = { along: 7.5, across: 10, turn: 0.5 } as const;
export const HANDS = { along: 10, across: 9 } as const;
export const HEAD = { along: 2.5 } as const;
export const BRANCHES = { count: 7, from: Math.PI * 0.55, step: Math.PI * 0.15, root: 2 } as const;
const BRANCH = { long: 20, short: 15, twigAt: 0.6, twigTurn: 0.7, twig: 5 } as const;
export const ROOTS = {
  count: 4,
  from: Math.PI * 0.6,
  spread: Math.PI * 0.8,
  start: 9,
  base: 6,
  step: 3,
} as const;
export const ROOT_LENGTH = 24;

function units(draw: (ctx: Ctx) => void): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.save();
    ctx.scale(UNIT, UNIT);
    draw(ctx);
    ctx.restore();
  };
}

function fillRim(ctx: Ctx, fill: string, width: number): void {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = width;
  ctx.strokeStyle = OUT;
  ctx.stroke();
}

function grain(ctx: Ctx, x: number, y: number, radius: number, from: number, to: number) {
  ctx.beginPath();
  ctx.arc(x, y, radius, from, to);
  ctx.lineWidth = 0.8;
  ctx.strokeStyle = GRAIN;
  ctx.stroke();
}

function knee(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, 6.6, 4.4, 0, 0, TAU);
  fillRim(ctx, BARK, 1.4);
}

function bodyPath(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, 8.5, 13, 0, 0, TAU);
}

function body(ctx: Ctx): void {
  bodyPath(ctx);
  fillRim(ctx, BARK, 2);
  for (let row = -3; row <= 3; row += 1) {
    grain(ctx, -12, row * 3.4, 12, -0.35, 0.35);
  }
}

function silhouette(ctx: Ctx): void {
  bodyPath(ctx);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

function hand(ctx: Ctx): void {
  circle(ctx, 3.2);
  fillRim(ctx, BARK, 1.3);
}

function head(ctx: Ctx): void {
  circle(ctx, 6.4);
  fillRim(ctx, BARK, 1.6);
  grain(ctx, -4.5, 0, 7, -0.6, 0.6);
  grain(ctx, -3, 0, 6, -0.7, 0.7);
}

function branch(length: number): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(length, 0);
    ctx.lineWidth = 3.2;
    ctx.strokeStyle = OUT;
    ctx.stroke();
    ctx.lineWidth = 1.6;
    ctx.strokeStyle = BARK;
    ctx.stroke();
    const at = length * BRANCH.twigAt;
    ctx.beginPath();
    ctx.moveTo(at, 0);
    ctx.lineTo(
      at + Math.cos(BRANCH.twigTurn) * BRANCH.twig,
      Math.sin(BRANCH.twigTurn) * BRANCH.twig,
    );
    ctx.lineWidth = 1.1;
    ctx.stroke();
  };
}

function leaves(length: number): (ctx: Ctx) => void {
  const at = length * BRANCH.twigAt;
  const spots: readonly (readonly [number, number, number, string])[] = [
    [length, 0, 3.4, SHADE_SOFT],
    [
      at + Math.cos(BRANCH.twigTurn) * BRANCH.twig,
      Math.sin(BRANCH.twigTurn) * BRANCH.twig,
      2.4,
      WHITE,
    ],
    [length - 4 + Math.cos(-0.9) * 3, Math.sin(-0.9) * 3, 2.2, SHADE_SOFT],
  ];
  return (ctx) => {
    for (const [x, y, radius, fill] of spots) {
      circle(ctx, radius, x, y);
      fillRim(ctx, fill, 0.6);
    }
  };
}

function root(turn: number): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.lineCap = 'round';
    const steps = 8;
    for (let step = 0; step < steps; step += 1) {
      const point = (share: number) => [
        share * ROOT_LENGTH,
        0.25 *
          ((ROOTS.start + share * ROOT_LENGTH) * Math.sin(share * 3 + turn) -
            ROOTS.start * Math.sin(turn)),
      ];
      const [x0 = 0, y0 = 0] = point(step / steps);
      const [x1 = 0, y1 = 0] = point((step + 1) / steps);
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.lineWidth = 3.2 + (0.8 - 3.2) * (step / steps);
      ctx.strokeStyle = GRAIN;
      ctx.stroke();
    }
  };
}

function soft(width: number, height: number): Piece {
  return [
    96,
    96,
    REFERENCE_RADIUS,
    (ctx) => {
      glow(ctx, WHITE, 8);
      ctx.fillStyle = WHITE;
      ctx.beginPath();
      ctx.ellipse(0, 0, width, height, 0, 0, TAU);
      ctx.fill();
    },
  ];
}

function based(reach: number, height: number, draw: (ctx: Ctx) => void): Piece {
  return [2 * reach * UNIT, height * UNIT, REFERENCE_RADIUS, units(draw)];
}

const FOX_FUR = '#ff8a3d';
const FOX_TIP = '#fff6dc';
const OWL_FEATHERS = '#c9a06a';
const OWL_WINGS = '#a07a50';
const EYE = '#1a120c';

function poly(ctx: Ctx, points: readonly (readonly [number, number])[]): void {
  ctx.beginPath();
  for (const [x, y] of points) {
    ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function foxBody(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, 6, 3.6, 0, 0, TAU);
  fillRim(ctx, FOX_FUR, 1.2);
  circle(ctx, 3, 5.5, 0);
  fillRim(ctx, FOX_FUR, 1);
  for (const side of [-1, 1]) {
    poly(ctx, [
      [5, 2 * side],
      [6.5, 5 * side],
      [7.5, 1.5 * side],
    ]);
    fillRim(ctx, FOX_FUR, 0.8);
  }
  circle(ctx, 0.9, 8.4, 0);
  ctx.fillStyle = EYE;
  ctx.fill();
}

function foxTail(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(-4, 0, 5.5, 2.8, 0, 0, TAU);
  fillRim(ctx, FOX_FUR, 1);
  ctx.beginPath();
  ctx.ellipse(-8, 0, 2.2, 1.8, 0, 0, TAU);
  ctx.fillStyle = FOX_TIP;
  ctx.fill();
}

function owlBody(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, 6, 5, 0, 0, TAU);
  fillRim(ctx, OWL_FEATHERS, 1.2);
  circle(ctx, 3.6, 4, 0);
  fillRim(ctx, OWL_FEATHERS, 1);
  for (const side of [-1, 1]) {
    poly(ctx, [
      [3.5, 3 * side],
      [4.5, 5.5 * side],
      [5.5, 2.5 * side],
    ]);
    ctx.fillStyle = OWL_WINGS;
    ctx.fill();
  }
}

function owlWing(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(-1, 7, 5, 9, 0.3, 0, TAU);
  fillRim(ctx, OWL_WINGS, 1.1);
}

export const FOX_TAIL_AT = -6;
export const OWL_WING_AT = 4;

export const SPIRIT_PIECES = {
  foxBody: [36, 24, REFERENCE_RADIUS, units(foxBody)],
  foxTail: [44, 16, REFERENCE_RADIUS, units(foxTail)],
  owlBody: [34, 30, REFERENCE_RADIUS, units(owlBody)],
  owlWing: [36, 72, REFERENCE_RADIUS, units(owlWing)],
  spiritShadow: soft(14, 14),
} satisfies Record<string, Piece>;

export const GROUND_ROOT = 64;

function groundRoot(ctx: Ctx): void {
  ctx.lineCap = 'round';
  ctx.strokeStyle = WHITE;
  const steps = 16;
  for (let step = 0; step < steps; step += 1) {
    const [x0, y0] = groundPoint(step / steps);
    const [x1, y1] = groundPoint((step + 1) / steps);
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.lineWidth = 7 + (1.4 - 7) * (step / steps);
    ctx.stroke();
  }
  for (const [share, side, length] of ROOTLETS) {
    const [x, y] = groundPoint(share);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.quadraticCurveTo(
      x + length * 0.5,
      y + side * length * 0.2,
      x + length,
      y + side * length * 0.6,
    );
    ctx.lineWidth = 2.2;
    ctx.stroke();
  }
}

const ROOTLETS: readonly (readonly [number, number, number])[] = [
  [0.35, 1, 16],
  [0.6, -1, 12],
];

export function groundPoint(share: number): [number, number] {
  return [share * GROUND_ROOT, Math.sin(share * 4.2) * 7 * share];
}

export const GROUND_PIECES = {
  groundRoot: [2 * GROUND_ROOT + 8, 44, REFERENCE_RADIUS, groundRoot],
} satisfies Record<string, Piece>;

export const ENRACINE_PIECES = {
  ...SPIRIT_PIECES,
  knee: [32, 24, REFERENCE_RADIUS, units(knee)],
  body: [40, 58, REFERENCE_RADIUS, units(body)],
  silhouette: [40, 58, REFERENCE_RADIUS, units(silhouette)],
  hand: [16, 16, REFERENCE_RADIUS, units(hand)],
  head: [30, 30, REFERENCE_RADIUS, units(head)],
  branchLong: based(BRANCH.long + 2, 10, branch(BRANCH.long)),
  branchShort: based(BRANCH.short + 2, 10, branch(BRANCH.short)),
  leavesLong: based(BRANCH.long + 4, 16, leaves(BRANCH.long)),
  leavesShort: based(BRANCH.short + 4, 16, leaves(BRANCH.short)),
  rootA: based(ROOT_LENGTH + 2, 36, root(0)),
  rootB: based(ROOT_LENGTH + 2, 36, root(1.1)),
  shadow: soft(18, 24),
} satisfies Record<string, Piece>;

export function branchLength(index: number): number {
  return index % 2 === 1 ? BRANCH.long : BRANCH.short;
}
